import { RiMore2Line, RiPencilLine } from '@remixicon/react';
import { default as BusinessEntityRoles } from '@shared/constants/BusinessEntityRoles';
import { useMemo } from 'react';
import BoldHeader from 'src/components/atoms/grid/BoldHeader';
import { AgDropdownItem, AgDropdownMenu, AgSwitch } from 'src/components/ui';
import { GridColObj } from 'src/modules/grids';
import { appLinks } from 'src/routing/appLinks';
import { linkCell } from 'src/utils/grid/cell/cellRenderers';
import type { BusinessEntityRow as BusinessEntityModel } from './useBusinessEntities';

type UseBusinessEntityColumnsProps = {
  customFields: GridColObj | null;
  isAccountingEnabled: boolean;
  onToggleDisabled: (entity: BusinessEntityModel) => void;
  onEdit: (entity: BusinessEntityModel) => void;
};

export function useBusinessEntityColumns({
  customFields,
  isAccountingEnabled,
  onToggleDisabled,
  onEdit,
}: UseBusinessEntityColumnsProps): GridColObj<BusinessEntityModel> {
  return useMemo(
    (): GridColObj<BusinessEntityModel> => ({
      name: {
        headerName: 'Name',
        minWidth: 180,
        flex: 1,
        type: 'string',
        renderHeader: BoldHeader,
        renderCell: (params) => {
          const link = linkCell(() => appLinks.settingsEntities({ id: params.row.id }));
          return link(params);
        },
      },
      roles: {
        headerName: 'Roles',
        minWidth: 180,
        flex: 2,
        type: 'string',
        renderHeader: BoldHeader,
        customFilterType: 'string_array',
        getFilterSelectOptions: Object.entries(BusinessEntityRoles).map(([value, label]) => ({ value, label })),
        renderCell: ({ value }) => (
          <div className="flex gap-1">
            {value?.map((role: keyof typeof BusinessEntityRoles) => (
              <span className="badge" key={role}>
                {BusinessEntityRoles[role]}
              </span>
            ))}
          </div>
        ),
      },
      bluebookId: {
        headerName: 'Bluebook Id',
        minWidth: 180,
        flex: 1,
        type: 'string',
        filterable: false,
        renderHeader: BoldHeader,
      },
      ...(isAccountingEnabled
        ? {
            // Filterable on the account name; not sortable, because the server cannot page that order
            // without skipping entities that have no account (businessEntities.repo `toBusinessEntityPageArgs`).
            receivablesAccount: {
              headerName: 'Receivables Account',
              minWidth: 180,
              flex: 1,
              type: 'string',
              sortable: false,
              renderHeader: BoldHeader,
              valueGetter: (_value, row) => row.receivablesAccount?.accountName || '',
            },
            payablesAccount: {
              headerName: 'Payables Account',
              minWidth: 180,
              flex: 1,
              type: 'string',
              sortable: false,
              renderHeader: BoldHeader,
              valueGetter: (_value, row) => row.payablesAccount?.accountName || '',
            },
          }
        : {}),
      ...(customFields ?? {}),
      actions: {
        type: 'actions',
        width: 140,
        disableExport: true,
        renderCell: ({ row }) => (
          <>
            <AgSwitch isSelected={!row.disabled} onChange={() => onToggleDisabled(row)} />
            <AgDropdownMenu
              size="icon-sm"
              icon={<RiMore2Line size={20} />}
              variant="ghost"
              excludeFromTabOrder
              items={
                <AgDropdownItem icon={<RiPencilLine size={16} />} onAction={() => onEdit(row)}>
                  Edit
                </AgDropdownItem>
              }
            />
          </>
        ),
      },
    }),
    [customFields, isAccountingEnabled, onToggleDisabled, onEdit]
  );
}
